import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { photoFrame, photos } from '../../../content/media';

const differentiators = [
  {
    icon: 'ri-medal-line',
    title: 'Certified IT Experts',
    desc: 'Our team holds industry certifications across Microsoft, AWS, Google Cloud, Cisco, and cybersecurity frameworks.',
  },
  {
    icon: 'ri-radar-line',
    title: 'Proactive Monitoring',
    desc: '24/7 monitoring with AI-powered alerting that identifies and resolves issues before they impact your business.',
  },
  {
    icon: 'ri-expand-width-line',
    title: 'Scalable Solutions',
    desc: 'Technology frameworks built to grow with you — from 10 to 10,000 employees without missing a beat.',
  },
  {
    icon: 'ri-speed-line',
    title: 'Fast Response Times',
    desc: '15-minute average response time for critical issues. We measure our performance by your satisfaction.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Security-First Mindset',
    desc: 'Every solution we design, deploy, and manage incorporates security at every layer — no exceptions.',
  },
  {
    icon: 'ri-trophy-line',
    title: '6+ Years of Excellence',
    desc: 'Over a decade of delivering results for businesses across healthcare, finance, retail, and more.',
  },
];



export default function WhyChooseUs() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 bg-white overflow-hidden">
      <div className="mx-auto px-6 lg:px-10 xl:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          {/* Left — Image */}
          <div className="relative">
            <div className={`${photoFrame} h-[380px]`}>
              <img
                src={photos.team}
                alt="Our Expert Team"
                className="w-full h-full object-cover object-center"
              />
            </div>


          </div>

          {/* Right — Content */}
          <div>
            <span className="eyebrow">Why Partner With Us</span>
            <h2 className="text-[26px] lg:text-[34px] font-extrabold text-page-ink tracking-[-0.03em] leading-tight mb-3">
              Technology Partners You Can Actually Trust
            </h2>
            <p className="text-page-muted text-sm leading-relaxed mb-6 font-light">
              We don't just manage your IT — we take ownership of your technology outcomes. Proactive support, honest advice, and solutions that genuinely serve your business goals.
            </p>

            {/* Differentiators */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {differentiators.map((d, i) => (
                <div
                  key={d.title}
                  className={`flex items-start gap-4 transition-all duration-600 ${
                    visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
                  }`}
                  style={{ transitionDelay: `${i * 70}ms` }}
                >
                  <div className="w-9 h-9 flex items-center justify-center bg-[#c97a2b]/10 border border-[#c97a2b]/20 rounded-xl flex-shrink-0 mt-0.5">
                    <i className={`${d.icon} text-[#c97a2b] text-base`} />
                  </div>
                  <div>
                    <h4 className="font-bold text-page-ink text-[14px] tracking-[-0.01em] mb-1">{d.title}</h4>
                    <p className="text-page-muted text-xs leading-relaxed">{d.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <Link to="/why-choose-us" className="btn-primary">
                See All Differentiators <i className="ri-arrow-right-line text-sm" />
              </Link>
            </div>
          </div>
        </div>


      </div>
    </section>
  );
}
